/** The console's brand tokens for Ant Design, per theme.
 *
 *  The primary accent is one colour in both themes. Links are not: indigo-600
 *  on the dark theme's surfaces reads at about 2.4:1, below WCAG AA's 4.5:1
 *  for text (the release rehearsal's R-2, a Get started link), so the dark
 *  theme's links take a lighter seed. antd's dark algorithm shifts it (this
 *  seed renders as #8087d8: 5.0:1 on the elevated surface #1f1f1f, more on
 *  the others); `brandTheme.test.ts` holds the RENDERED token to AA. */
export const BRAND_PRIMARY = "#4f46e5";
export const BRAND_LINK_DARK = "#939bfa";

/** The surfaces text sits on: antd's dark container and layout, and white. */
export const DARK_SURFACES = ["#141414", "#000000", "#1f1f1f"] as const;
export const LIGHT_SURFACES = ["#ffffff", "#f5f5f5"] as const;

/** Q59 (founder, 2026-10-26: BS-05/BS-06 fixed console-wide as one change). Every text
 *  colour antd derives below WCAG AA on this console was measured in the browser-smoke runs
 *  of 2026-10-14; these replace them in both themes, and `brandTheme.test.ts` holds each to
 *  4.5:1 on the surface it is drawn on. */
const LIGHT_TEXT = {
  colorTextSecondary: "#595959", // was #8c8c8c, 3.35:1 on white
  colorTextDescription: "#595959",
  colorTextPlaceholder: "#737373", // was #bfbfbf, 1.83:1
  colorSuccessText: "#237804", // green tags (Active, Allowed, In force): was 3.37:1 on #f6ffed
  colorWarningText: "#874d00", // gold tags: was 2.76:1 on #fffbe6
  colorError: "#cf1322", // danger text on white and white on it: was 3.27:1
  colorErrorText: "#cf1322",
} as const;
const DARK_TEXT = {
  colorTextPlaceholder: "#8c8c8c", // was 2.24:1 on #141414
  colorError: "#ff7875", // danger in drawers: was 4.24:1
  colorErrorText: "#ff7875",
} as const;

export function brandTokens(isDark: boolean): {
  colorPrimary: string;
  colorLink: string;
} & Partial<Record<keyof typeof LIGHT_TEXT, string>> {
  return {
    colorPrimary: BRAND_PRIMARY,
    colorLink: isDark ? BRAND_LINK_DARK : BRAND_PRIMARY,
    ...(isDark ? DARK_TEXT : LIGHT_TEXT),
  };
}

/** BS-05: on the dark theme the active tab's label and the current page number took the
 *  accent itself (#463fc6 on #141414, 2.44:1); they take the dark theme's link colour. */
export function brandComponents(isDark: boolean) {
  if (!isDark) return {};
  return {
    Tabs: { itemSelectedColor: BRAND_LINK_DARK, itemActiveColor: BRAND_LINK_DARK },
    Pagination: { itemActiveColor: BRAND_LINK_DARK },
  };
}

function luminance(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.x contrast ratio of two `#rrggbb` colours. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}
